import styled from '@emotion/styled';
import { ui } from '@libs/domain';
import { useItemNames } from '@ps-smart-cp/ppfw-client';
import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
  position: relative;
`;

const InputLabel = styled.label`
  font-size: 14px;
  margin-bottom: 5px;
  color: #333;
`;

const InputText = styled.input`
  width: 300px;
  height: 30px;
  padding: 3px 35px 3px 8px;
  font-size: 14px;
  border: 1px solid #cccccc;
  border-radius: 5px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #666;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 8px;
  top: 75%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #333;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #666;
  }
`;

interface LoginInputFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onEnter: () => void;
  type?: 'password' | 'text';
  name?: string;
}

export function LoginInputField({
  id,
  label,
  value,
  onChange,
  onEnter,
  type = 'text',
  name,
}: LoginInputFieldProps) {
  const [isShowPassword, setIsShowPassword] = useState(false);
  const isLoadingRef = useRef(false);

  const labelNames = useItemNames(ui.labelNames);

  const togglePasswordVisibility = () => {
    setIsShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isLoadingRef.current) {
      isLoadingRef.current = true;
      try {
        await onEnter();
      } finally {
        isLoadingRef.current = false;
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <InputWrapper>
        <InputLabel htmlFor={id}>{label}</InputLabel>
        <InputText
          id={id}
          type={type === 'password' && !isShowPassword ? 'password' : 'text'}
          value={value}
          onChange={onChange}
          name={name}
        />
        {type === 'password' && (
          <ToggleButton type="button" onClick={togglePasswordVisibility}>
            {isShowPassword ? labelNames('hidden') : labelNames('display')}
          </ToggleButton>
        )}
      </InputWrapper>
    </form>
  );
}
